import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { X } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import { ResizablePanelGroup, ResizablePanel } from "@/components/ui/resizable";
import { ChatListItem } from "./chat-archive/ChatListItem";
import { ChatSearch } from "./chat-archive/ChatSearch";
import { useIsMobile } from "@/hooks/use-mobile";

interface SavedChat {
  id: string;
  title: string;
}

interface ChatArchiveProps {
  savedChats: SavedChat[];
  onSelectChat: (chatId: string) => Promise<void>;
  onDeleteChat: (chatId: string) => Promise<void>;
  onRenameChat: (chatId: string, newTitle: string) => Promise<void>;
  onClose?: () => void;
}

export function ChatArchive({
  savedChats,
  onSelectChat,
  onDeleteChat,
  onRenameChat,
  onClose
}: ChatArchiveProps) {
  const [chatToDelete, setChatToDelete] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const [searchQuery, setSearchQuery] = useState("");
  
  const filteredChats = savedChats?.filter(chat => 
    chat.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDelete = async (chatId: string) => {
    try {
      await onDeleteChat(chatId);
      setChatToDelete(null);
      toast({
        title: "Chat slettet",
        description: "Chatten har blitt slettet fra arkivet.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Feil ved sletting",
        description: "Kunne ikke slette chatten. Vennligst prøv igjen.",
      });
    }
  };

  const handleSelect = async (chatId: string) => {
    try {
      await onSelectChat(chatId);
      if (isMobile && onClose) {
        onClose();
      }
      toast({
        title: "Chat lastet",
        description: "Chatten har blitt lastet inn.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Feil ved lasting",
        description: "Kunne ikke laste chatten. Vennligst prøv igjen.",
      });
    }
  };

  const handleRename = async (chatId: string, newTitle: string) => {
    try {
      await onRenameChat(chatId, newTitle);
      toast({
        title: "Navn oppdatert",
        description: "Chattens navn har blitt oppdatert.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Feil ved navnendring",
        description: "Kunne ikke endre navnet. Vennligst prøv igjen.",
      });
    }
  };

  return (
    <ResizablePanelGroup direction="horizontal">
      <ResizablePanel 
        defaultSize={isMobile ? 100 : 25}
        minSize={isMobile ? 100 : 20}
        maxSize={isMobile ? 100 : 40}
        className="border-l h-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
      >
        <div className="p-4 flex flex-col gap-4 h-full">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">Arkiv</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <ChatSearch searchQuery={searchQuery} onSearchChange={setSearchQuery} />
          
          <ScrollArea className="flex-1 w-full">
            <div className="pr-4">
              {filteredChats?.map((chat) => (
                <ChatListItem
                  key={chat.id}
                  id={chat.id}
                  title={chat.title}
                  onSelect={handleSelect}
                  onDelete={(id) => setChatToDelete(id)}
                  onRename={handleRename}
                />
              ))}
            </div>
          </ScrollArea>

          <AlertDialog open={!!chatToDelete} onOpenChange={() => setChatToDelete(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Er du sikker?</AlertDialogTitle>
                <AlertDialogDescription>
                  Denne handlingen kan ikke angres. Dette vil permanent slette chatten fra arkivet.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Avbryt</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => chatToDelete && handleDelete(chatToDelete)}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Slett
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}